import React from 'react';
import SearchableSelect from '../ui/SearchableSelect';
import FilterPopover, { FilterSelectSpec } from '../ui/FilterPopover';

/**
 * The breadcrumb-bar filters that used to be two or three separate selects,
 * now behind one Filters popover (count badge, chips, Clear all). Each takes
 * the same value/onChange pairs the bar already had, so the page state and what
 * it writes are unchanged; only the container differs. A bar that is left with a
 * single select (an optional one is hidden when it has no options) keeps that
 * plain select, as before.
 */

interface Opt { value: string; label: string }

function SelectFilters({ selects }: { selects: FilterSelectSpec[] }) {
  if (selects.length === 1) {
    const s = selects[0];
    return (
      <div className="header-filter-group">
        <SearchableSelect className="header-filter-select" value={s.value} onChange={s.onChange} options={s.options} />
      </div>
    );
  }
  return <FilterPopover selects={selects} />;
}

const QUOTE_STATUSES: Opt[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

export const QuotesFilters: React.FC<{
  status: string; onStatusChange: (v: string) => void;
  category: string; onCategoryChange: (v: string) => void;
  categories: { id: string; name: string }[];
}> = ({ status, onStatusChange, category, onCategoryChange, categories }) => {
  const selects: FilterSelectSpec[] = [
    { key: 'status', label: 'Status', options: QUOTE_STATUSES, value: status, defaultValue: 'all', onChange: onStatusChange },
  ];
  if (categories.length > 0) {
    selects.push({
      key: 'category', label: 'Category', value: category, defaultValue: '', onChange: onCategoryChange, searchable: categories.length > 8,
      options: [{ value: '', label: 'All Categories' }, ...categories.map(c => ({ value: c.id, label: c.name }))],
    });
  }
  return <SelectFilters selects={selects} />;
};

const PAYMENT_STATUSES: Opt[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'not-started', label: 'Not Started' },
  { value: 'partially-paid', label: 'Partially Paid' },
  { value: 'fully-paid', label: 'Fully Paid' },
  { value: 'overdue', label: 'Overdue' },
];

export const PaymentsFilters: React.FC<{
  event: string; onEventChange: (v: string) => void;
  status: string; onStatusChange: (v: string) => void;
  events: { id: string; title: string }[];
}> = ({ event, onEventChange, status, onStatusChange, events }) => {
  const selects: FilterSelectSpec[] = [];
  if (events.length > 0) {
    selects.push({
      key: 'event', label: 'Event', value: event, defaultValue: '', onChange: onEventChange,
      options: [{ value: '', label: 'All Events' }, ...events.map(ev => ({ value: ev.id, label: ev.title }))],
    });
  }
  selects.push({ key: 'status', label: 'Status', options: PAYMENT_STATUSES, value: status, defaultValue: 'all', onChange: onStatusChange });
  return <SelectFilters selects={selects} />;
};

const DECOR_CATEGORIES: Opt[] = [
  { value: 'ALL', label: 'All Categories' },
  { value: 'FLORALS', label: 'Florals' },
  { value: 'LIGHTING', label: 'Lighting' },
  { value: 'RECEPTION - COCKTAIL HOUR', label: 'Reception / Cocktail Hour' },
  { value: 'SPECIAL ELEMENTS', label: 'Special Elements' },
  { value: 'TRADITIONAL MARRIAGE', label: 'Traditional Marriage' },
  { value: 'CHURCH', label: 'Church' },
  { value: 'WEDDING COLORS', label: 'Theme Colors' },
  { value: 'OTHER', label: 'Other' },
];
const DECOR_STATUSES: Opt[] = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'planned', label: 'Planned' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'installed', label: 'Installed' },
];

export const DecorFilters: React.FC<{
  category: string; onCategoryChange: (v: string) => void;
  status: string; onStatusChange: (v: string) => void;
  event: string; onEventChange: (v: string) => void;
  events: { id: string; title: string }[];
}> = ({ category, onCategoryChange, status, onStatusChange, event, onEventChange, events }) => {
  const selects: FilterSelectSpec[] = [
    { key: 'category', label: 'Category', options: DECOR_CATEGORIES, value: category, defaultValue: 'ALL', onChange: onCategoryChange },
    { key: 'status', label: 'Status', options: DECOR_STATUSES, value: status, defaultValue: 'ALL', onChange: onStatusChange },
  ];
  if (events.length > 0) {
    selects.push({
      key: 'event', label: 'Event', value: event, defaultValue: '', onChange: onEventChange,
      options: [{ value: '', label: 'All Events' }, ...events.map(ev => ({ value: ev.id, label: ev.title }))],
    });
  }
  return <SelectFilters selects={selects} />;
};

const INVENTORY_FILTERS: Opt[] = [
  { value: 'all', label: 'All Items' },
  { value: 'available', label: 'Received' },
  { value: 'sold_out', label: 'Not received' },
];

export const ProcurementFilters: React.FC<{
  vendors: { id: string; name: string }[];
  vendorId: string | null; onVendorChange: (v: string | null) => void;
  filter: 'all' | 'available' | 'sold_out'; onFilterChange: (v: 'all' | 'available' | 'sold_out') => void;
}> = ({ vendors, vendorId, onVendorChange, filter, onFilterChange }) => {
  const selects: FilterSelectSpec[] = [];
  if (vendors.length > 0) {
    // The old vendor select had no "all" entry; null is the state the page
    // already treats as "no vendor filter", so the popover can offer it.
    selects.push({
      key: 'vendor', label: 'Vendor', value: vendorId ?? '', defaultValue: '', onChange: v => onVendorChange(v || null), searchable: vendors.length > 8,
      options: [{ value: '', label: 'All vendors' }, ...vendors.map(v => ({ value: v.id, label: v.name }))],
    });
  }
  selects.push({
    key: 'items', label: 'Items', options: INVENTORY_FILTERS, value: filter, defaultValue: 'all',
    onChange: v => onFilterChange(v as 'all' | 'available' | 'sold_out'),
  });
  return <SelectFilters selects={selects} />;
};

